
import { int32ToString, round } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { printf, toText } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { SVGAttr, CSSProp } from "../fable_modules/Fable.React.9.4.0/Fable.React.Props.fs.js";
import { length, ofArray } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, string_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import * as react from "react";
import { keyValueList } from "../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import { render } from "../Common/KeyCaps.js";
import { idShortParts } from "../Common/KeyTypes.js";
import { isMac } from "../Interface/Bridge.js";

export const Constants_dividerBarWidth = 10;

/**
 * Round a CSS length so that it lands on a whole DEVICE pixel.
 * 
 * **An edge that falls between two device pixels is painted blended.** The browser covers the
 * boundary pixel only partly, so it comes out a mixture of what lies on each side of it, and a
 * seam between two flat colours becomes a hairline of a third. On the canvas that reads as a fine
 * line of the sheet's background along an edge - loudest in the Light theme, whose background is
 * a pale yellow against a white pane and a grey rule.
 * 
 * It is not a rounding error in this code. The fixed widths here are whole CSS pixels and Windows
 * display scaling is what turns them into fractions of a device pixel - but those are the same
 * every run, and land on the grid at the scalings in use. The one that does not is the width of
 * the right-hand pane in the waveform simulator, which is whatever the user dragged the divider
 * to: at 125% scaling a 573-pixel pane is 716.25 device pixels, so the seam between the pane and
 * the canvas falls a quarter of a pixel inside one, and that pixel is painted part pane and part
 * canvas.
 * 
 * Only lengths that decide such a SEAM should come through here. Snapping one that does not is
 * worse than leaving it: the header's height is 58 CSS pixels and the rule below it 2 more, and
 * it is where the rule ENDS - at 60 - that the canvas background begins. Rounding the 58 on its
 * own moves that boundary off the grid and paints exactly the hairline this exists to remove,
 * which is what happened when it was first tried here.
 * 
 * The ratio is read at each call rather than once, because it changes when the window is dragged
 * to a monitor with different scaling.
 */
export function toWholeDevicePixels(cssPx) {
    const dpr = window.devicePixelRatio;
    if (dpr > 0) {
        return round(cssPx * dpr) / dpr;
    }
    else {
        return cssPx;
    }
}

/**
 * A CSS length, snapped, as a string React can use.
 */
export function px(cssPx) {
    const arg = toWholeDevicePixels(cssPx);
    return toText(printf("%.4fpx"))(arg);
}

const headerHeightCss = 58;

const rightSectionWidthS = 400;

const rightSectionWidthL = 650;

export const minViewerWidth = 400;

export function minEditorWidth() {
    return ~~(document.getElementById("WholeApp").offsetWidth * 0.25) | 0;
}

export const rightSectionWidthViewerDefault = 650;

/**
 * The header's height. NOT snapped to device pixels, and that matters: the seam the eye picks up
 * is where the canvas BACKGROUND begins, which is the inner edge of the rule below the header -
 * (58 + 2) CSS pixels, a whole number of device pixels at every scaling in use. Snapping the 58
 * on its own moves that inner edge off the grid and paints the hairline this was meant to remove.
 */
export function getHeaderHeight() {
    return headerHeightCss;
}

export function headerHeight() {
    return toText(printf("%.4fpx"))(headerHeightCss);
}

export function rightSectionWidthCss(model) {
    const matchValue = model.RightPaneTabVisible;
    switch (matchValue.tag) {
        case 3:
            return rightSectionWidthL;
        case 2: {
            const matchValue_1 = model.SimSubTabVisible;
            switch (matchValue_1.tag) {
                case 2:
                case 1:
                    return model.WaveSimViewerWidth;
                default:
                    return rightSectionWidthL;
            }
        }
        default:
            return rightSectionWidthS;
    }
}

export function rightSectionWidth(model) {
    return px(rightSectionWidthCss(model));
}

export function leftSectionWidth(model) {
    let arg_1;
    return ["style", {
        width: (arg_1 = rightSectionWidth(model), toText(printf("calc(100%s - %s - 10px)"))("%")(arg_1)),
    }];
}

export function navbarStyle(model) {
    return ["style", {
        width: "100%",
        height: headerHeight(),
    }];
}

/**
 * For making Sheet contained inside left section (don't want sheet behind right section tabs) NOT USED
 */
export function leftSectionStyle(model) {
    return ["style", {
        position: "fixed",
        left: "0px",
        top: "0px",
        height: "100%",
        width: leftSectionWidth(model),
        overflowX: "hidden",
        overflowY: "hidden",
        borderTop: "2px solid lightgray",
        userSelect: "none",
        zIndex: 31,
        backgroundColor: "white",
    }];
}

export function rightSectionStyle(model) {
    return ["style", {
        position: "fixed",
        right: "0px",
        top: "0px",
        height: "100%",
        width: rightSectionWidth(model),
        overflowX: "visible",
        overflowY: "visible",
        borderTop: "2px solid lightgray",
        userSelect: "none",
        zIndex: 31,
        backgroundColor: "white",
    }];
}

/**
 * Fill what is left of the height below a header of the given size, scrolling if the content does
 * not fit. The overflow is a parameter rather than always Auto because the caller needs to switch
 * it off for a moment, and saying so with a second Style prop does not work: React keeps the last
 * style it is given and drops the rest, so the height quietly went with it - which left the right
 * tab body with no height at all, and nothing in it able to scroll.
 */
export function belowHeaderStyle(headerSize, overflow) {
    return ["style", {
        overflowY: overflow,
        height: `calc(100% - ${headerSize})`,
    }];
}

export function canvasVisibleStyle(model) {
    const widthRightSec = rightSectionWidth(model);
    return ["style", {
        display: "block",
        position: "absolute",
        overflowX: "scroll",
        overflowY: "scroll",
        top: headerHeight(),
        left: "0px",
        bottom: "0px",
        right: widthRightSec,
        borderTop: "2px solid lightgray",
    }];
}

export function canvasVisibleStyleList(model) {
    let background;
    const matchValue = model.Sheet.Wire.Symbol.Theme;
    background = ((matchValue.tag === 1) ? (new CSSProp(/* BackgroundColor */ 21, ["rgba(255,255,0,0.1)"])) : ((matchValue.tag === 2) ? (new CSSProp(/* BackgroundColor */ 21, ["rgba(0,0,0,0.05)"])) : (new CSSProp(/* BackgroundColor */ 21, ["white"]))));
    const widthRightSec = rightSectionWidth(model);
    return ofArray([new CSSProp(/* Display */ 125, ["block"]), new CSSProp(/* Position */ 291, ["absolute"]), new CSSProp(/* OverflowX */ 271, ["scroll"]), new CSSProp(/* OverflowY */ 272, ["scroll"]), new CSSProp(/* Top */ 365, [headerHeight()]), new CSSProp(/* Left */ 208, ["0px"]), new CSSProp(/* Bottom */ 83, ["0px"]), new CSSProp(/* Right */ 298, [widthRightSec]), new CSSProp(/* BorderTop */ 76, ["2px solid lightgray"]), background]);
}

/**
 * What is written across the top of the canvas about the sheet below it: the read-only banner and
 * the sheet-kind legend, one under the other.
 * 
 * They are stacked in a flex column rather than each given its own offset from the top of the
 * canvas, because either can be absent and neither knows whether the other is showing - an offset
 * would be a number that has to be kept in step with the height of something else.
 * 
 * Over the canvas rather than above it, so that nothing moves when one of them appears, and
 * taking no pointer events: this sits on top of the schematic, and a band the mouse could not
 * draw through would be worse than no banner.
 */
export function canvasTopOverlayStyle(model) {
    return ["style", {
        position: "absolute",
        top: headerHeight(),
        left: "0px",
        right: rightSectionWidth(model),
        pointerEvents: "none",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
    }];
}

/**
 * The colours one kind of sheet is written in. Backgrounds are translucent so that the schematic
 * under them still reads, which is what makes these safe to leave on screen the whole time.
 */
export class SheetKindColours extends Record {
    constructor(Text$, Background, Border) {
        super();
        this.Text = Text$;
        this.Background = Background;
        this.Border = Border;
    }
}

export function SheetKindColours_$reflection() {
    return record_type("DiagramStyle.SheetKindColours", [], SheetKindColours, () => [["Text", string_type], ["Background", string_type], ["Border", string_type]]);
}

export const arraySheetColours = new SheetKindColours("#4b2e83", "rgba(147,112,219,0.18)", "rgba(102,51,153,0.45)");

export const librarySheetColours = new SheetKindColours("#7a3e00", "rgba(255,159,64,0.20)", "rgba(200,110,20,0.5)");

export const canvasReadOnlyBannerStyle = ["style", {
    alignSelf: "stretch",
    padding: "4px 10px",
    fontSize: "12px",
    textAlign: "center",
    color: librarySheetColours.Text,
    backgroundColor: librarySheetColours.Background,
    borderBottom: "1px solid " + librarySheetColours.Border,
    whiteSpace: "nowrap",
    overflowX: "hidden",
    textOverflow: "ellipsis",
}];

/**
 * One chip of the legend in the top-left corner of the canvas, saying what kind of sheet is on
 * screen. A label on the schematic and not something to press, so it is translucent and lets the
 * mouse through, as the buttons in the opposite corner do not.
 */
export function canvasSheetKindStyle(colours) {
    return ["style", {
        marginTop: "6px",
        marginLeft: "10px",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "600",
        color: colours.Text,
        backgroundColor: colours.Background,
        border: "1px solid " + colours.Border,
        whiteSpace: "nowrap",
    }];
}

export const canvasSmallMenuStyle = ["style", {
    display: "block",
    position: "absolute",
    overflowY: "hidden",
    left: "10px",
    bottom: "25px",
    whiteSpace: "nowrap",
}];

export const canvasSmallButtonStyle = ["style", {
    marginRight: "5px",
    backgroundColor: "white",
    borderRadius: "4px",
    borderStyle: "solid",
    outline: "none",
    padding: "4px",
    opacity: 0.7,
}];

export const notificationStyle = ["style", {
    zIndex: 100,
    position: "absolute",
    userSelect: "none",
    right: "20px",
    bottom: "20px",
}];

export const simulationNumberStyle = ["style", {
    width: "320px",
    height: "30px",
}];

export const constraintNumberStyle = ["style", {
    width: "200px",
    height: "30px",
}];

export const simulationBitStyle = ["style", {
    width: "100px",
    height: "30px",
    paddingTop: "3px",
}];

export const menuLabelStyle = ["style", {
    outline: "none",
    marginTop: "10px",
    marginBottom: "10px",
    color: "#7a7a7a",
    fontSize: "0.80em",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
}];

export const sortArrowStyle = ["style", {
    margin: "0",
    display: "block",
    width: "100%",
    height: "50%",
    padding: "0 0 0 0",
    top: "0",
    fontSize: "50%",
    position: "relative",
    borderColor: "white",
}];

export const colMoveArrowStyle = ["style", {
    margin: "0",
    display: "block",
    width: "100%",
    height: "50%",
    padding: "0 0 0 0",
    top: "0",
    fontSize: "80%",
    position: "relative",
    borderColor: "white",
}];

export function ttGridColumnProps(index) {
    return ofArray([new CSSProp(/* Border */ 33, ["1px solid gray"]), new CSSProp(/* Padding */ 273, ["7px"]), new CSSProp(/* FontSize */ 153, ["18px"]), new CSSProp(/* TextAlign */ 325, ["left"]), new CSSProp(/* GridColumnStart */ 176, [int32ToString(index + 1)]), new CSSProp(/* GridColumnEnd */ 174, [int32ToString(index + 2)]), new CSSProp(/* Transition */ 372, ["all 0.2s ease-in-out"])]);
}

export function ttGridHiddenColumnProps(gridWidth) {
    return ofArray([new CSSProp(/* GridColumnStart */ 176, [int32ToString(gridWidth + 1)]), new CSSProp(/* GridColumnEnd */ 174, [int32ToString(gridWidth + 2)]), new CSSProp(/* Width */ 395, [0]), new CSSProp(/* OverflowX */ 271, ["hidden"]), new CSSProp(/* Visibility */ 383, ["hidden"])]);
}

export function ttGridContainerStyle(model) {
    const visibleCols = max(1, model.TTConfig.IOOrder.length - length(model.TTConfig.HiddenColumns)) | 0;
    return ["style", {
        display: "grid",
        gridAutoFlow: "column",
        width: "100%",
        gridTemplateColumns: toText(printf("repeat(%d, minmax(0, 1fr))"))(visibleCols),
    }];
}

/**
 * display react of refresh button with color (e.g. white) at given height (e.g. 10px)
 */
export function refreshSvg(color, height) {
    let props;
    const children_2 = [(props = [new SVGAttr(/* D */ 3, ["M496 48V192c0 17.69-14.31 32-32 32H320c-17.69 0-32-14.31-32-32s14.31-32\r\n                32-32h63.39c-29.97-39.7-77.25-63.78-127.6-63.78C167.7 96.22 96 167.9 96 256s71.69\r\n                159.8 159.8 159.8c34.88 0 68.03-11.03 95.88-31.94c14.22-10.53 34.22-7.75 44.81\r\n                6.375c10.59 14.16 7.75 34.22-6.375 44.81c-39.03 29.28-85.36 44.86-134.2 44.86C132.5\r\n                479.9 32 379.4 32 256s100.5-223.9 223.9-223.9c69.15 0 134 32.47 176.1 86.12V48c0-17.69\r\n                14.31-32 32-32S496 30.31 496 48z"]), ["style", {
        fill: color,
        stroke: color,
        strokeWidth: "5px",
    }]], react.createElement("path", keyValueList(props, 1)))];
    return react.createElement("svg", {
        viewBox: "0 0 512 512",
        height: height,
    }, ...children_2);
}

/**
 * A pushpin, upright when it is holding something and tilted when it is not - the same shape in
 * both states, so the icon is recognised once and then read by its angle rather than by comparing
 * two drawings. Colour reinforces it: grey while the menu is free to close, dark once pinned.
 */
export function pinSvg(pinned) {
    let props;
    const props_2 = [new SVGAttr(/* ViewBox */ 40, ["0 0 24 24"]), new SVGAttr(/* Height */ 14, ["16px"]), new SVGAttr(/* Width */ 41, ["16px"]), ["style", {
        display: "block",
        transform: pinned ? "none" : "rotate(-45deg)",
    }]];
    const children_2 = [(props = [new SVGAttr(/* D */ 3, ["M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0\r\n                   1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"]), ["style", {
        fill: pinned ? "#363636" : "#8a8a8a",
    }]], react.createElement("path", keyValueList(props, 1)))];
    return react.createElement("svg", keyValueList(props_2, 1), ...children_2);
}

export const emptyRefreshSVG = react.createElement("svg", {
    height: "20",
    width: "20",
});

export const zoomOutSVG = (() => {
    const children_4 = [react.createElement("path", {
        d: "M190.707,180.101l-47.079-47.077c11.702-14.072,18.752-32.142,18.752-51.831C162.381,36.423,125.959,0,81.191,0\r\n                C36.422,0,0,36.423,0,81.193c0,44.767,36.422,81.187,81.191,81.187c19.689,0,37.759-7.049,51.831-18.75l47.079,47.077\r\n                c1.464,1.465,3.384,2.197,5.303,2.197c1.919,0,3.839-0.732,5.303-2.197C193.637,187.778,193.637,183.03,190.707,180.101z\r\n                M15,81.193C15,44.694,44.693,15,81.191,15c36.497,0,66.189,29.694,66.189,66.193c0,36.496-29.692,66.187-66.189,66.187\r\n                C44.693,147.38,15,117.689,15,81.193z",
    }), react.createElement("path", {
        d: "M118.035,73.689H44.346c-4.142,0-7.5,3.358-7.5,7.5c0,4.142,3.358,7.5,7.5,7.5h73.689c4.142,0,7.5-3.358,7.5-7.5\r\n                    C125.535,77.047,122.177,73.689,118.035,73.689z",
    })];
    return react.createElement("svg", {
        viewBox: "0 0 192.904 192.904",
        height: "20px",
    }, ...children_4);
})();

export const zoomInSVG = (() => {
    const children_4 = [react.createElement("path", {
        d: "M190.707,180.101l-47.079-47.077c11.702-14.072,18.752-32.142,18.752-51.831C162.381,36.423,125.959,0,81.191,0\r\n                C36.422,0,0,36.423,0,81.193c0,44.767,36.422,81.187,81.191,81.187c19.689,0,37.759-7.049,51.831-18.75l47.079,47.077\r\n                c1.464,1.465,3.384,2.197,5.303,2.197c1.919,0,3.839-0.732,5.303-2.197C193.637,187.778,193.637,183.03,190.707,180.101z\r\n                M15,81.193C15,44.694,44.693,15,81.191,15c36.497,0,66.189,29.694,66.189,66.193c0,36.496-29.692,66.187-66.189,66.187\r\n                C44.693,147.38,15,117.689,15,81.193z",
    }), react.createElement("path", {
        d: "M118.035,73.689H88.69V44.345c0-4.142-3.357-7.5-7.5-7.5s-7.5,3.358-7.5,7.5v29.345H44.346c-4.143,0-7.5,3.358-7.5,7.5\r\n                c0,4.142,3.357,7.5,7.5,7.5H73.69v29.346c0,4.142,3.357,7.5,7.5,7.5s7.5-3.358,7.5-7.5V88.689h29.345c4.143,0,7.5-3.358,7.5-7.5\r\n                C125.535,77.047,122.178,73.689,118.035,73.689z",
    })];
    return react.createElement("svg", {
        viewBox: "0 0 192.904 192.904",
        height: "20px",
    }, ...children_4);
})();

export function colorSpan(color, text) {
    const props = [["style", {
        color: color,
    }]];
    return react.createElement("span", keyValueList(props, 1), text);
}

/**
 * The keys to press for a shortcut on the platform this is running on, drawn as keys.
 * 
 * Read off the shortcut table, never written out, for the same reason the shortcut list in Info is
 * generated from it: prose that names a key cannot be held to what the key dispatcher does, and
 * the two chords the window size warning was first written with said Ctrl and Alt on a Mac, where
 * they are Cmd and Opt.
 */
export function keysOf(id) {
    return render(idShortParts(isMac, id));
}

